import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { SectionSetting } from '@/types/database.types'
import type { LayoutData, LayoutSection } from '@/types/database.types'
import { getVariantKeyFromConfig } from '@/lib/cms/component-registry'
/** Generate a UUID v4 without external dependency */
const genId = () => crypto.randomUUID()

/* ============================================================
   usePageLayout — Visual Builder Layout Management
   Reads layout from section_settings with variant_key support.
   Provides save/publish operations for the builder.
   ============================================================ */

/** Build a LayoutData from section_settings rows */
function settingsToLayout(settings: SectionSetting[]): LayoutData {
  return {
    sections: settings.map((s) => ({
      id: s.id,
      section_key: s.section_key,
      variant_key: getVariantKeyFromConfig(s.config),
      enabled: s.enabled,
      config: (s.config && typeof s.config === 'object' ? s.config : {}) as Record<string, unknown>,
    })),
  }
}

/** Read the current layout for a tenant (built from section_settings) */
export function usePageLayout(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['page-layout', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('section_settings')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('display_order', { ascending: true })

      if (error) throw error
      const settings = (data ?? []) as SectionSetting[]
      return settingsToLayout(settings)
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  })
}

/** Save layout changes — updates section_settings with variant_key in config */
export function useSaveLayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      sections,
    }: {
      tenantId: string
      sections: LayoutSection[]
    }) => {
      // Update each section's config (variant_key), enabled, and display_order
      const promises = sections.map((section, idx) => {
        const config = { ...(section.config || {}), variant_key: section.variant_key }

        if (section.id.startsWith('new-')) {
          // Insert new section
          return supabase.from('section_settings').insert({
            id: genId(),
            tenant_id: tenantId,
            section_key: section.section_key,
            enabled: section.enabled,
            display_order: idx,
            config,
          } as never)
        }

        // Update existing section
        return supabase.from('section_settings')
          .update({
            enabled: section.enabled,
            display_order: idx,
            config,
          } as never)
          .eq('id', section.id)
      })

      const results = await Promise.all(promises)
      const failed = results.find((r: { error?: unknown }) => r.error)
      if (failed?.error) throw failed.error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-layout'] })
      qc.invalidateQueries({ queryKey: ['section-settings'] })
    },
  })
}

/** Remove a section from the layout */
export function useRemoveSection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ sectionId }: { sectionId: string }) => {
      const { error } = await supabase.from('section_settings')
        .delete()
        .eq('id', sectionId)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-layout'] })
      qc.invalidateQueries({ queryKey: ['section-settings'] })
    },
  })
}
